import {
    AddUserButton,
    Avatar,
    AvatarGroup,
    ChatContainer,
    Conversation,
    ConversationHeader,
    ConversationList,
    MainContainer,
    Search,
} from '@chatscope/chat-ui-kit-react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {AutoComplete} from 'primereact/autocomplete'
import {Button} from 'primereact/button'
import {Sidebar} from 'primereact/sidebar'
import {Tooltip} from 'primereact/tooltip'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import {withRouter} from 'react-router-dom'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import useWindowSize from '../../utils/useWindowSize'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId, setIsOpenChat} from '../authentication/AuthUtils'
import FlexBox from '../layout/FlexBox'
import PageContentWrapper from '../layout/PageContentWrapper'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {refetchEvent} from '../table-utils/TableUtils'
import Chat from './Chat'
import './Chat.css'
import {
    createOrUpdateChatGenericInfo,
    getChatPageGenericInfo,
    readChatMessage,
} from './ChatApi'
import {getUserListForConversation} from './ChatEndpoint'
import {useChatContext} from './ChatProvider'
import {
    chatEvent,
    removeReceiverRoomMessageChangeListener,
    removeSenderRoomMessageChange,
} from './ChatService'

import {Badge} from 'primereact/badge'
import {ReactComponent as UndrawChatImg} from '../../assets/images/undraw_begin_chat.svg'
const PATH = 'room'
const QueryKey = 'Rooms'

const USER_PATH = 'user'
const UserKey = 'Users'

const ConversationQueryKey = 'Conversation'

const Room = ({hcpData = []}) => {
    const {translateCommons} = TranslateUtils()
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {changeActiveRoom, activeParticipantId, activeRoom} = useChatContext()
    const {currentHospital} = useUserHospital()
    const queryClient = useQueryClient()
    const pageRows = 10
    const [pageNumber, setPageNumber] = useState(0)
    const [globalSearchValue, setGlobalSearchValue] = useState('')
    const width = useWindowSize()

    const hospitalId = useMemo(() => {
        if (userRole === SystemRole.USER) {
            return user.hospitalId
        }

        return currentHospital?.id
    }, [currentHospital?.id, user.hospitalId, userRole])

    const {
        data,
        isLoading,
        isFetching,
        isFetched,
        refetch: refetchRooms,
    } = useQuery(
        [QueryKey, hospitalId, user?.id, globalSearchValue],
        async () =>
            await getChatPageGenericInfo(PATH, user?.id, 0, pageRows, '', -1, {
                globalSearchValue: globalSearchValue,
                hospitalId: hospitalId,
            }),
        {
            enabled:
                !!user?.id &&
                (userRole === SystemRole.ADMIN ||
                    (!!getClientId() && !!hospitalId)),
        },
    )

    const {mutate: fetchMoreRooms, isLoading: isLoadingFetchMore} = useMutation(
        async () =>
            await getChatPageGenericInfo(
                PATH,
                user?.id,
                pageNumber + 1,
                pageRows,
                'id',
                -1,
                {
                    globalSearchValue: globalSearchValue,
                    hospitalId: hospitalId,
                },
            ),
        {
            onSuccess: data => {
                if (data && data.content) {
                    queryClient.setQueryData(
                        [QueryKey, hospitalId, user?.id, globalSearchValue],
                        oldData => {
                            return oldData && oldData?.content
                                ? {
                                      ...data,
                                      content: oldData
                                          ? [
                                                ...oldData.content,
                                                ...data.content,
                                            ]
                                          : [...data.content],
                                  }
                                : {
                                      content: [],
                                  }
                        },
                    )

                    setPageNumber(data.pageable.pageNumber)
                }
            },
        },
    )

    const handleFetchMore = useCallback(() => {
        if (
            !!user?.id &&
            !!getClientId() &&
            !!data &&
            !data?.last &&
            !isLoadingFetchMore &&
            !isLoading
        ) {
            fetchMoreRooms()
        }
    }, [user, data, fetchMoreRooms, isLoading, isLoadingFetchMore])

    const [roomData, setRoomData] = useState({})

    const [addNewConvo, setAddNewConvo] = useState(false)
    const [filterUser, setFilterUser] = useState('')
    const [visible, setVisible] = useState(false)
    const {data: userList} = useQuery(
        // eslint-disable-next-line no-sparse-arrays
        [
            UserKey + typeof filterUser === 'string'
                ? filterUser
                : filterUser?.usernameOrEmail?.data,
            ,
            user?.id,
        ],
        () => [],
    )

    const {mutate: mutateUsers} = useMutation(
        async () =>
            await getUserListForConversation(
                // USER_PATH,
                user?.id,
                {
                    usernameOrEmail:
                        typeof filterUser === 'string'
                            ? filterUser
                            : filterUser?.usernameOrEmail?.data,
                    excludedId: user?.id,
                    byPassRole: true,
                    hospitalId: hospitalId,
                    conversation: true,
                    systemRole: userRole,
                },
                null,
                true,
            ),
        {
            onSuccess: data => {
                if (data && Array.isArray(data)) {
                    queryClient.setQueryData(
                        // eslint-disable-next-line no-sparse-arrays
                        [
                            UserKey + typeof filterUser === 'string'
                                ? filterUser
                                : filterUser?.usernameOrEmail?.data,
                            ,
                            user?.id,
                        ],
                        () => [...data],
                    )
                }
            },
        },
    )

    const handleRefetchSuggestions = () => {
        mutateUsers()
    }

    const handleCreateOrUpdateRoom = useCallback(
        async userId => {
            let toPostData = {
                creatorId: user?.id,
                userIdList: [userId],
                hospitalId: hospitalId,
            }
            await createOrUpdateChatGenericInfo(
                PATH,
                user?.id,
                toPostData,
            ).then(data => {
                if (data?.id) {
                    setAddNewConvo(false)
                    setRoomData(data)
                    setPageNumber(0)
                    setFilterUser('')
                    refetchRooms()

                    // context
                    changeActiveRoom(data)
                }
            })
        },
        [changeActiveRoom, hospitalId, refetchRooms, user?.id],
    )

    const renderItemTemplate = useCallback(item => {
        return (
            <div className={'flex align-items-center'}>
                <div className="mr-3">
                    <Avatar
                        className="avatar-sm"
                        src={
                            item?.extraData ||
                            'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
                        }
                    />
                </div>
                <div className={'flex flex-column user-convo'}>
                    <h6 className="font-bold">{item.label}</h6>
                    {item.data ? <div>{item.data}</div> : null}
                </div>
            </div>
        )
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelectRoom = useCallback(item => {
        // Context
        changeActiveRoom(item)
        setRoomData(item)
        readChatMessage(item.participantId)
        queryClient.setQueryData(
            [QueryKey, hospitalId, user?.id, globalSearchValue],
            oldData => {
                let tmp =
                    oldData && isArrayAndNotEmpty(oldData?.content)
                        ? [...oldData?.content]
                        : []
                if (tmp.length > 0) {
                    tmp.forEach((itemOldData, idx) => {
                        if (itemOldData.id === item.id) {
                            tmp[idx].unreadCount = 0
                        }
                    })
                }

                return {
                    ...data,
                    content: [...tmp],
                }
            },
        )
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelfReloadRooms = useCallback(() => {
        refetchRooms()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        chatEvent.on('SENDER-ROOM-MESSAGE-CHANGE', handleSelfReloadRooms)

        return () => {
            removeSenderRoomMessageChange()
        }
    }, [handleSelfReloadRooms])

    const handleOnRoomMessageChange = useCallback(
        async payload => {
            let roomId = payload?.roomId

            if (!!roomId) {
                let roomIds = data?.content.map(x => x.id)
                if (roomIds?.length > 0 && roomIds?.includes(roomId)) {
                    if (roomData?.id === roomId) {
                        const res = await readChatMessage(
                            roomData?.participantId,
                        )
                        if (res === HTTP_STATUS_OK) {
                            refetchRooms()
                        }
                    } else {
                        refetchRooms()
                    }
                }
            }
        },
        [data?.content, refetchRooms, roomData?.id, roomData?.participantId],
    ) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (data) {
            if (data?.content && data.content?.length > 0) {
                chatEvent.on('RECEIVER-ROOM-MESSAGE-CHANGE', payload =>
                    handleOnRoomMessageChange(payload),
                )
            } else {
                chatEvent.on('RECEIVER-ROOM-MESSAGE-CHANGE', payload =>
                    refetchRooms(),
                )
            }

            return () => removeReceiverRoomMessageChangeListener()
        }
    }, [data, handleOnRoomMessageChange, refetchRooms])

    useEffect(() => {
        if (
            !!activeParticipantId &&
            activeRoom &&
            Object.keys(activeRoom).length > 0
        ) {
            handleSelectRoom(activeRoom)
        }
    }, [activeParticipantId, activeRoom, handleSelectRoom])

    const handleReloadRooms = useCallback(() => {
        setPageNumber(0)
        refetchRooms()
    }, [refetchRooms])

    useEffect(() => {
        if (!!user?.id) {
            refetchEvent.on(
                'REFETCH-DATA-TABLE-' + ConversationQueryKey + '-' + user?.id,
                handleReloadRooms,
            )

            return () => {
                refetchEvent.removeListener(
                    'REFETCH-DATA-TABLE-' +
                        ConversationQueryKey +
                        '-' +
                        user?.id,
                )
            }
        }
    }, [handleReloadRooms, user])

    useEffect(() => {
        const handleBeforeUnload = () => {
            setIsOpenChat(false)
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [])

    const renderAvatarGroup = (avatars, name, max = 1) => {
        if (!isArrayAndNotEmpty(avatars) || avatars.length === 1) {
            return renderSingleAvatar(
                name,
                avatars?.length === 1 && avatars[0] ? avatars[0] : '',
            )
        }

        return (
            <AvatarGroup className="flex-nowrap" size="md">
                {avatars?.map(
                    (avatar, idx) =>
                        idx < 2 && (
                            <Avatar
                                key={idx}
                                src={
                                    avatar ||
                                    'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
                                }
                                style={
                                    idx === 1
                                        ? {
                                              marginLeft:
                                                  avatars.length > 1
                                                      ? '-25px'
                                                      : '20px',
                                              //   marginTop: '-20px',
                                          }
                                        : {
                                              marginLeft: '-25px',
                                              //   marginTop: '-10px',
                                          }
                                }
                            />
                        ),
                )}
            </AvatarGroup>
        )
    }

    const renderSingleAvatar = (name, avatar) => {
        const getInitials = (name = '') => {
            let parts = name.length > 1 ? name.replace(/[^a-zA-Z0-9]/g, '') : ''

            if (parts.length > 1) {
                parts = parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
            }

            return parts.length > 1 ? parts.toUpperCase() : ' '
        }

        const initials = getInitials(name)
        return avatar ? (
            <Avatar src={avatar} />
        ) : (
            <Avatar
                style={{
                    backgroundColor: 'darkcyan',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                }}>
                {initials}
            </Avatar>
        )
    }

    const renderUserChatList = useCallback(
        () => {
            let suggestionList = []

            if (userRole === SystemRole.USER && isArrayAndNotEmpty(hcpData)) {
                let roomDataList = isArrayAndNotEmpty(data?.content)
                    ? data.content
                    : []
                let hcpIds = []

                hcpData.forEach(x => {
                    if (x.hcpId) {
                        hcpIds.push(x.hcpId)
                    }
                })

                if (roomDataList.length > 0 && hcpIds.length > 0) {
                    for (let i = 0; i < roomDataList.length; i++) {
                        if (
                            isArrayAndNotEmpty(roomDataList[i].userIds) &&
                            !roomDataList[i].supportTeamId
                        ) {
                            hcpIds = hcpIds.filter(
                                x => !roomDataList[i].userIds.includes(x),
                            )
                        }
                    }
                }
                if (hcpIds.length > 0) {
                    hcpData.forEach(item => {
                        if (hcpIds.includes(item.hcpId)) {
                            if (item.hcpId) {
                                let itemSuggestion = {
                                    name: item.hcpName,
                                    userId: item.hcpId,
                                    avatar: item.mainRefAvatar,
                                    groupChat: false,
                                }

                                suggestionList.push(itemSuggestion)
                            }
                        }
                    })
                }
            }

            return (
                <div position="left ">
                    <InfiniteScroll
                        pageStart={pageNumber}
                        loadMore={handleFetchMore}
                        hasMore={!data?.last}
                        loader={
                            data &&
                            !data?.last &&
                            isFetching &&
                            !!isLoadingFetchMore ? (
                                <div className="loader" key={0}>
                                    {translateCommons('Loading')} ...
                                </div>
                            ) : null
                        }
                        useWindow={false}
                        threshold={120}>
                        <FlexBox className="justify-content-between">
                            <h3 className="font-bold text-darkcyan">
                                Conversations
                            </h3>
                            {userRole !== SystemRole.USER && (
                                <ConversationHeader.Actions>
                                    <div className="">
                                        <Tooltip
                                            target={'#addConvo'}
                                            content={translateCommons(
                                                'Add new conversation',
                                            )}
                                        />
                                        <AddUserButton
                                            id="addConvo"
                                            style={{fontSize: '15px'}}
                                            onClick={() => setAddNewConvo(true)}
                                        />
                                    </div>
                                </ConversationHeader.Actions>
                            )}
                        </FlexBox>
                        <FlexBox className=" w-23rem pr-2">
                            <Search
                                className="bg-gray-50 w-full font-bold"
                                placeholder={`${translateCommons('Search')}...`}
                                value={globalSearchValue || ''}
                                onChange={value => {
                                    setGlobalSearchValue(value)
                                }}
                                onClearClick={() => setGlobalSearchValue('')}
                                // disabled={data?.content?.length < 1}
                            />
                        </FlexBox>
                        {/* <Divider color="#6ea9d7" /> */}
                        {data?.content?.length > 0 && <h6>My Conversations</h6>}

                        <ConversationList
                            className="border-top-1 border-gray-100"
                            style={{width: '100%'}}>
                            {!!data &&
                                data?.content?.length > 0 &&
                                data.content?.map(item => (
                                    <Conversation
                                        key={`${item?.id}`}
                                        onClick={() => handleSelectRoom(item)}
                                        className={`${
                                            item?.id &&
                                            roomData?.id &&
                                            item.id === roomData.id
                                                ? 'active-room'
                                                : ''
                                        }  flex align-items-center relative`}>
                                        {renderAvatarGroup(
                                            item?.avatars,
                                            item.name,
                                            2,
                                        )}

                                        <Conversation.Content className="">
                                            <div className="">
                                                <h5
                                                    className={`${
                                                        item.unreadCount > 0 &&
                                                        'font-bold'
                                                    } mb-1 p-0 text-overflow-ellipsis`}>
                                                    {item?.name}
                                                    {` `}
                                                    {!!item?.groupChat &&
                                                        item.supportTeamName && (
                                                            <span className="bg-blue-500 p-1 text-base border-round-sm text-white">
                                                                <i className="pi pi-users" />{' '}
                                                                {` `}
                                                                {
                                                                    item.supportTeamName
                                                                }
                                                            </span>
                                                        )}{' '}
                                                </h5>
                                                <p
                                                    className={`${
                                                        item.unreadCount > 0 &&
                                                        'font-bold'
                                                    } mb-0`}>
                                                    {item?.description}
                                                </p>

                                                {item.unreadCount > 0 && (
                                                    <div className="absolute right-0 top-0  p-2 h-full flex align-items-center">
                                                        <Badge
                                                            severity="danger"
                                                            value={
                                                                item.unreadCount ||
                                                                0
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </Conversation.Content>
                                    </Conversation>
                                ))}
                        </ConversationList>
                        {!isLoading &&
                            userRole === SystemRole.USER &&
                            !!isArrayAndNotEmpty(suggestionList) && (
                                <React.Fragment>
                                    {' '}
                                    <h6>Suggestions</h6>
                                    <ConversationList
                                        className="border-top-1 border-gray-100"
                                        style={{width: '100%'}}>
                                        {!!suggestionList &&
                                            suggestionList.length > 0 &&
                                            suggestionList.map((item, idx) => (
                                                <Conversation
                                                    key={`${idx}`}
                                                    name={
                                                        item?.groupChat
                                                            ? `${item?.name}`
                                                            : item?.name
                                                    }
                                                    info={item?.description}
                                                    onClick={() =>
                                                        handleCreateOrUpdateRoom(
                                                            item.userId,
                                                        )
                                                    }
                                                    className={`${
                                                        item?.id &&
                                                        roomData?.id &&
                                                        item.id === roomData.id
                                                            ? 'active-room'
                                                            : ''
                                                    } flex align-items-center`}>
                                                    {renderSingleAvatar(
                                                        item.name,
                                                        item?.avatar,
                                                    )}
                                                </Conversation>
                                            ))}
                                    </ConversationList>
                                </React.Fragment>
                            )}
                    </InfiniteScroll>
                </div>
            )
        },
        [
            data,
            globalSearchValue,
            handleCreateOrUpdateRoom,
            handleFetchMore,
            handleSelectRoom,
            hcpData,
            isFetching,
            isLoading,
            isLoadingFetchMore,
            pageNumber,
            roomData.id,
            translateCommons,
            userRole,
        ],
        [],
    )

    const renderSideBarConservation = useCallback(() => {
        return width <= 770 ? (
            <Sidebar
                visible={visible}
                onHide={() => handleHideChatList()}
                style={{width: '70%'}}>
                <div className="pt-3"></div>
                {renderUserChatList()}
            </Sidebar>
        ) : (
            renderUserChatList()
        )
    }, [width, visible, renderUserChatList])

    const handleShowChatList = () => {
        blockOverflownScroll()
        setVisible(true)
    }

    const handleHideChatList = () => {
        unblockOverflownScroll()
        setVisible(false)
    }
    return (
        <PageContentWrapper>
            <div
                className="col-12"
                style={{
                    position: 'relative',
                    height: 'calc(100vh - 8rem)',
                    fontSize: '2em',
                }}>
                <MainContainer responsive className="main-radius">
                    {renderSideBarConservation()}
                    {!!addNewConvo && (
                        <React.Fragment>
                            <ChatContainer>
                                <ConversationHeader>
                                    <ConversationHeader.Content>
                                        <div className="flex justify-content-between align-items-center">
                                            <div
                                                className={
                                                    'flex align-items-center'
                                                }
                                                style={{minHeight: '42px'}}>
                                                <div className="mr-2">
                                                    <h6 className="mb-0">
                                                        {translateCommons('To')}
                                                        :{' '}
                                                    </h6>
                                                </div>
                                                <div>
                                                    {/* Search */}
                                                    <AutoComplete
                                                        id="custom"
                                                        field="label"
                                                        className="no-outline mb-0"
                                                        placeholder={translateCommons(
                                                            'Enter username or email',
                                                        )}
                                                        suggestions={
                                                            userList || []
                                                        }
                                                        completeMethod={
                                                            handleRefetchSuggestions
                                                        }
                                                        value={filterUser}
                                                        onChange={e => {
                                                            setFilterUser(
                                                                e.value,
                                                            )
                                                        }}
                                                        onSelect={({
                                                            value: data,
                                                        }) => {
                                                            handleCreateOrUpdateRoom(
                                                                data.value,
                                                            )
                                                        }}
                                                        itemTemplate={
                                                            renderItemTemplate
                                                        }
                                                        autoFocus
                                                        forceSelection
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                className="pointer"
                                                onClick={() =>
                                                    setAddNewConvo(false)
                                                }>
                                                <i className="pi pi-times"></i>
                                            </div>
                                        </div>
                                    </ConversationHeader.Content>
                                </ConversationHeader>
                            </ChatContainer>
                        </React.Fragment>
                    )}
                    {!addNewConvo &&
                        roomData &&
                        roomData?.id &&
                        isArrayAndNotEmpty(data?.content) && (
                            <div className="flex w-full">
                                <Chat
                                    roomData={roomData || {}}
                                    roomId={roomData?.id}
                                    onShowChatList={handleShowChatList}
                                />
                            </div>
                        )}
                    {!addNewConvo &&
                        data?.content &&
                        data?.content.length > 0 &&
                        (!activeRoom || !activeRoom?.id) &&
                        (!roomData || !roomData?.id) && (
                            <React.Fragment>
                                <ChatContainer>
                                    <ConversationHeader
                                        style={{
                                            height: '100%',
                                            borderBottom: 'unset',
                                            backgroundColor: 'unset',
                                        }}>
                                        <ConversationHeader.Content>
                                            <FlexBox
                                                className={
                                                    'justify-content-between'
                                                }>
                                                <Button
                                                    className={`p-1 mr-2 text-3xl ${
                                                        width >= 770
                                                            ? 'hidden'
                                                            : 'flex'
                                                    }`}
                                                    style={{
                                                        backgroundColor:
                                                            '#6ea9d7',
                                                    }}
                                                    icon="pi pi-bars"
                                                    onClick={() =>
                                                        handleShowChatList()
                                                    }
                                                />
                                            </FlexBox>
                                            <FlexBox
                                                className={'flex-column'}
                                                style={{height: '100%'}}>
                                                <div>
                                                    <img
                                                        src={require('../../assets/images/Empty-Convo.png')}
                                                        alt="Select_Conversation"
                                                        style={{
                                                            height: '30vh',
                                                        }}
                                                    />
                                                </div>
                                                <div className="my-4">
                                                    <h6>
                                                        {translateCommons(
                                                            'Select a conversation to start messaging',
                                                        )}
                                                    </h6>
                                                </div>
                                            </FlexBox>
                                        </ConversationHeader.Content>
                                    </ConversationHeader>
                                </ChatContainer>
                            </React.Fragment>
                        )}
                    {!isLoading &&
                        !addNewConvo &&
                        (!data || !data?.content || data.content?.length < 1) &&
                        (!activeRoom || !activeRoom?.id) &&
                        (!roomData || !roomData?.id) && (
                            <React.Fragment>
                                <ChatContainer>
                                    <ConversationHeader
                                        style={{
                                            height: '100%',
                                            borderBottom: 'unset',
                                            backgroundColor: 'unset',
                                        }}>
                                        <ConversationHeader.Content>
                                            <FlexBox
                                                className={
                                                    'justify-content-between'
                                                }>
                                                <Button
                                                    className={`p-1 mr-2 text-3xl ${
                                                        width >= 770
                                                            ? 'hidden'
                                                            : 'flex'
                                                    }`}
                                                    style={{
                                                        backgroundColor:
                                                            '#6ea9d7',
                                                    }}
                                                    icon="pi pi-bars"
                                                    onClick={() =>
                                                        handleShowChatList()
                                                    }
                                                />
                                            </FlexBox>
                                            <FlexBox
                                                className={
                                                    'flex-column justify-content-center align-items-center'
                                                }
                                                style={{height: '100%'}}>
                                                <div className="w-30rem">
                                                    <UndrawChatImg className="w-full h-full" />
                                                </div>
                                                <div className="my-4">
                                                    <h4 className="text-800 ">
                                                        {translateCommons(
                                                            "Let's start a conversation. Shall we ?",
                                                        )}
                                                    </h4>
                                                </div>
                                            </FlexBox>
                                        </ConversationHeader.Content>
                                    </ConversationHeader>
                                </ChatContainer>
                            </React.Fragment>
                        )}
                </MainContainer>
            </div>
        </PageContentWrapper>
    )
}

export default withRouter(Room)
